import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { formatMoney as sharedFormatMoney } from '@/lib/money';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { cn } from '@/lib/utils';

interface TripOpt { id: string; name: string; clientId: string; clientName?: string | null; status?: string | null }
interface ClientOpt { id: string; name: string; status?: string | null }
interface UserOpt { id: string; name: string; email?: string | null; role?: string | null }

// ──────────────────────────────────────────────────────────
// Types (mirror apps/api/src/routes/transactions.ts response)
// ──────────────────────────────────────────────────────────

type Status = 'MARKED' | 'UNMARKED' | 'SNOOZED' | 'ASKED';

interface AttributorRef {
  id?: string | null;
  name?: string | null;
  role?: string | null;
}
interface RefLite { id: string; name?: string | null }
interface AttributionInfo {
  type?: string | null;
  trip?: RefLite | null;
  client?: RefLite | null;
  bookingHead?: string | null;
  notes?: string | null;
}
interface AskState {
  from?: RefLite | null;
  to?: RefLite | null;
  askedAt: string;
  response?: 'YES_MINE' | 'NOT_ME' | null;
}
interface LedgerTxn {
  id: string;
  amount: string | null;
  currency?: string | null;
  amountFx?: string | null;
  currencyFx?: string | null;
  instrumentType: string;
  instrumentLabel: string;
  instrumentIcon: string;
  beneficiary?: string | null;
  vendor?: string | null;
  occurredAt: string;
  attributionStatus: Status;
  attributedBy?: AttributorRef | null;
  attribution?: AttributionInfo | null;
  askState?: AskState | null;
  snoozedUntil?: string | null;
  debitSmsBody?: string | null;
}
interface LedgerDay {
  date: string;
  totalCount: number;
  totalAmount: number;
  unmarkedCount: number;
  unmarkedAmount: number;
  transactions: LedgerTxn[];
}
interface LedgerResponse { days: LedgerDay[] }

// ──────────────────────────────────────────────────────────
// Formatting
// ──────────────────────────────────────────────────────────

function currencySymbol(cur?: string | null): string {
  const c = (cur ?? 'INR').toUpperCase();
  switch (c) {
    case 'INR': case '₹': return '₹';
    case 'USD': case '$': return '$';
    case 'GBP': case '£': return '£';
    case 'EUR': case '€': return '€';
    case 'AED': return 'AED ';
    case 'SGD': return 'S$';
    case 'JPY': case 'CNY': return '¥';
    case 'AUD': return 'A$';
    case 'CAD': return 'C$';
    case 'THB': return '฿';
    case 'IDR': return 'Rp ';
    case 'HKD': return 'HK$';
    case 'CHF': return 'CHF ';
    case 'MYR': return 'RM';
    case 'VND': return '₫';
    default: return `${c} `;
  }
}
function formatInr(n: number): string {
  // Native Intl.NumberFormat handles Indian grouping correctly.
  if (Number.isNaN(n)) return '—';
  return '₹' + n.toLocaleString('en-IN');
}
function formatMoney(s: string | null | undefined, cur?: string | null): string {
  if (s == null || s === '') return '—';
  const n = Number(s);
  const sym = currencySymbol(cur);
  if (Number.isNaN(n)) return `${sym}${s}`;
  return `${sym}${n.toLocaleString('en-IN')}`;
}
// Prefer the billing currency for FX-matched debits — a €533.97 Vueling
// booking should read as €533.97, not ₹45,381 (bank-side). Falls back to
// the single-side formatter above for rows without FX data (refund notes,
// legacy rows, etc.).
function formatTxnMoney(
  txn: { amount: string | null; currency?: string | null; amountFx?: string | null; currencyFx?: string | null },
  variant: 'billing' | 'bank' | 'both' = 'billing',
): string {
  return sharedFormatMoney(
    {
      amount: txn.amount,
      currency: txn.currency ?? 'INR',
      amountFx: txn.amountFx,
      currencyFx: txn.currencyFx,
    },
    variant,
  );
}
function formatAmount(s: string | null | undefined): string {
  return formatMoney(s, 'INR');
}
function formatDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return isoDate;
  const months = ['', 'Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${Number(d)} ${months[Number(m)]} ${y}`;
}
function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  } catch { return iso.slice(11, 16); }
}
function instrumentEmoji(icon: string): string {
  switch (icon) { case 'card': return '💳'; case 'bank': return '🏦'; case 'upi': return '📱'; case 'wallet': return '👛'; default: return '•'; }
}

// ──────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────

export function DebitsLedgerPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canReconcileRefunds = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';
  const [tab, setTab] = useState<'ledger' | 'refunds'>('ledger');
  const [status, setStatus] = useState<'ALL' | Status>('ALL');
  const [instrumentType, setInstrumentType] = useState<'ALL' | 'CARD' | 'BANK_TRANSFER'>('ALL');
  const [days, setDays] = useState(14);
  const [openTxnId, setOpenTxnId] = useState<string | null>(null);
  const refundCount = useQuery<{ count: number }>({
    queryKey: ['refund-notes', 'pending-count'],
    queryFn: () => api('/api/refund-notes/pending-count'),
    refetchInterval: 60_000,
  });

  const ledger = useQuery<LedgerResponse>({
    queryKey: ['debits-ledger', days, status, instrumentType],
    queryFn: () => api(`/api/transactions/daily?days=${days}&instrumentType=${instrumentType}&status=${status}`),
    refetchInterval: 60_000,
  });

  const openTxn = useMemo(() => {
    if (!openTxnId) return null;
    for (const d of ledger.data?.days ?? []) {
      const t = d.transactions.find((x) => x.id === openTxnId);
      if (t) return t;
    }
    return null;
  }, [openTxnId, ledger.data]);

  const onChanged = () => qc.invalidateQueries({ queryKey: ['debits-ledger'] });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Debits</h1>
        <p className="text-sm text-muted-foreground">
          Day-grouped ledger · marked vs unmarked at a glance · attribute your own or ask a teammate.
        </p>
      </div>

      <div className="flex gap-2 border-b">
        <button
          onClick={() => setTab('ledger')}
          className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === 'ledger' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Ledger
        </button>
        <button
          onClick={() => setTab('refunds')}
          className={`px-3 py-2 text-sm font-medium border-b-2 flex items-center gap-2 ${tab === 'refunds' ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          {canReconcileRefunds ? 'Refund inbox' : 'My refund notes'}
          {refundCount.data?.count ? (
            <span className="rounded-full bg-amber-500 text-white text-xs px-1.5 py-0.5 min-w-[18px] text-center">
              {refundCount.data.count}
            </span>
          ) : null}
        </button>
      </div>

      {tab === 'refunds' ? (
        <RefundInboxPanel
          canReconcile={canReconcileRefunds}
          onChanged={() => qc.invalidateQueries({ queryKey: ['refund-notes'] })}
        />
      ) : (
        <>
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-2">
            {(['ALL', 'UNMARKED', 'MARKED', 'ASKED', 'SNOOZED'] as const).map((s) => (
              <Button
                key={s}
                variant={status === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatus(s)}
              >
                {s === 'ALL' ? 'All' : s[0] + s.slice(1).toLowerCase()}
              </Button>
            ))}
            <div className="ml-4 h-6 w-px bg-border" />
            {(['ALL', 'CARD', 'BANK_TRANSFER'] as const).map((it) => (
              <Button
                key={it}
                variant={instrumentType === it ? 'default' : 'outline'}
                size="sm"
                onClick={() => setInstrumentType(it)}
              >
                {it === 'ALL' ? 'All instruments' : it === 'CARD' ? '💳 Cards' : '🏦 Bank'}
              </Button>
            ))}
            <div className="ml-4 flex items-center gap-2">
              <Label className="text-xs">Days:</Label>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="rounded border bg-transparent px-2 py-1 text-sm"
              >
                <option value={7}>7</option>
                <option value={14}>14</option>
                <option value={30}>30</option>
                <option value={90}>90</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Days */}
      {ledger.isLoading ? (
        <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (ledger.data?.days ?? []).length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">No debits in this range.</CardContent></Card>
      ) : (
        <DayList days={ledger.data!.days} onOpenTxn={(id) => setOpenTxnId(id)} />
      )}

      {/* Detail modal */}
      {openTxn && (
        <DetailModal txn={openTxn} onClose={() => setOpenTxnId(null)} onChanged={() => { onChanged(); }} />
      )}
        </>
      )}
    </div>
  );
}

function DayList({ days, onOpenTxn }: { days: LedgerDay[]; onOpenTxn: (id: string) => void }) {
  // Default: expanded. Track collapsed set so a click toggles.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  return (
    <div className="space-y-6">
      {days.map((day) => {
        const isExpanded = !collapsed.has(day.date);
        return (
          <div key={day.date}>
            <button
              type="button"
              onClick={() => {
                const next = new Set(collapsed);
                if (isExpanded) next.add(day.date); else next.delete(day.date);
                setCollapsed(next);
              }}
              className="mb-2 flex w-full items-center justify-between border-b pb-2 text-left transition hover:bg-muted/30"
            >
              <div>
                <div className="text-xs font-semibold uppercase text-muted-foreground">{formatDate(day.date)}</div>
                <div className="text-sm font-medium">{day.totalCount} debits · {formatInr(day.totalAmount)}</div>
                {day.unmarkedCount > 0 && (
                  <div className="text-xs text-red-600">
                    ⚠ {day.unmarkedCount} unmarked ({formatInr(day.unmarkedAmount)})
                  </div>
                )}
              </div>
              <span className="text-lg text-muted-foreground">{isExpanded ? '▾' : '▸'}</span>
            </button>
            {isExpanded && (
              <div className="divide-y rounded border">
                {day.transactions.map((t) => (
                  <DebitRow key={t.id} txn={t} onOpen={() => onOpenTxn(t.id)} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DebitRow({ txn, onOpen }: { txn: LedgerTxn; onOpen: () => void }) {
  const isUnmarked = txn.attributionStatus === 'UNMARKED';
  return (
    <button
      onClick={onOpen}
      className={cn(
        'flex w-full items-center gap-4 px-4 py-3 text-left transition hover:bg-muted/40',
        isUnmarked && 'bg-red-50/60',
      )}
    >
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold">{formatTxnMoney(txn, 'billing')}</div>
          <div className="text-xs text-muted-foreground">{instrumentEmoji(txn.instrumentIcon)} {txn.instrumentLabel}</div>
        </div>
        <div className="mt-0.5 text-sm">{txn.beneficiary ?? txn.vendor ?? '—'}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">
          {formatTime(txn.occurredAt)}
          {txn.attributedBy?.name && <> · by {txn.attributedBy.name}</>}
          {txn.attributionStatus === 'ASKED' && txn.askState?.to?.name && (
            <> · 🕐 Asked {txn.askState.to.name}</>
          )}
        </div>
        <div className="mt-2">
          <StatusPill txn={txn} />
        </div>
      </div>
    </button>
  );
}

function StatusPill({ txn }: { txn: LedgerTxn }) {
  const status = txn.attributionStatus;
  let label = '';
  let cls = '';
  if (status === 'MARKED') {
    const a = txn.attribution;
    const where =
      a?.trip?.name ? `Trip ${a.trip.name}`
      : a?.type === 'OFFICE_EXPENSE' ? `Vendor${txn.vendor ? ': ' + txn.vendor : ''}`
      : a?.type === 'TEAM_MEMBER' ? 'Team member'
      : a?.type === 'PERSONAL' ? 'Personal'
      : a?.type === 'OTHER' ? (a.notes ?? 'Other')
      : 'Marked';
    label = `✓ ${where}`;
    cls = 'bg-green-100 text-green-800';
  } else if (status === 'SNOOZED') {
    label = 'Snoozed';
    cls = 'bg-gray-100 text-gray-700';
  } else if (status === 'ASKED') {
    label = '🕐 Waiting on answer';
    cls = 'bg-amber-100 text-amber-800';
  } else {
    label = '⚠ Unmarked';
    cls = 'bg-red-100 text-red-700';
  }
  return <span className={cn('inline-block rounded px-2 py-0.5 text-xs font-medium', cls)}>{label}</span>;
}

// ──────────────────────────────────────────────────────────
// Detail modal (attribution form + ask flow)
// ──────────────────────────────────────────────────────────

function DetailModal({ txn, onClose, onChanged }: { txn: LedgerTxn; onClose: () => void; onChanged: () => void }) {
  const { user } = useAuth();
  const [mode, setMode] = useState<'CLIENT' | 'VENDOR' | 'TEAM_MEMBER' | 'PERSONAL' | 'OTHER'>('CLIENT');
  const [tripId, setTripId] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [vendorName, setVendorName] = useState(txn.vendor ?? '');
  const [teamMemberUserId, setTeamMemberUserId] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const [notes, setNotes] = useState('');
  const [askTo, setAskTo] = useState<string | null>(null);

  // Picker data — cached across modal opens by React Query.
  const trips = useQuery<TripOpt[]>({
    queryKey: ['picker-trips'],
    queryFn: () => api('/api/trips'),
    staleTime: 5 * 60_000,
  });
  const clients = useQuery<ClientOpt[]>({
    queryKey: ['picker-clients'],
    queryFn: () => api('/api/clients'),
    staleTime: 5 * 60_000,
  });
  const users = useQuery<UserOpt[]>({
    queryKey: ['picker-users'],
    queryFn: () => api('/api/users'),
    staleTime: 5 * 60_000,
  });

  const tripOptions = useMemo(() => {
    const rows = (trips.data ?? []).filter((t) => !clientId || t.clientId === clientId);
    return rows.map((t) => ({
      id: t.id,
      label: t.name,
      sub: [t.clientName, t.status].filter(Boolean).join(' · '),
    }));
  }, [trips.data, clientId]);
  const clientOptions = useMemo(
    () => (clients.data ?? []).map((c) => ({ id: c.id, label: c.name, sub: c.status ?? null })),
    [clients.data],
  );
  const teamOptions = useMemo(
    () => (users.data ?? [])
      .filter((u) => u.id !== user?.id)
      .map((u) => ({ id: u.id, label: u.name, sub: [u.role, u.email].filter(Boolean).join(' · ') })),
    [users.data, user?.id],
  );

  const attribute = useMutation({
    mutationFn: () =>
      api(`/api/transactions/${txn.id}/attribute`, {
        method: 'POST',
        body: {
          mode,
          tripId: tripId || null,
          clientId: clientId || null,
          vendorName: vendorName || null,
          teamMemberUserId: teamMemberUserId || null,
          freeText: freeText || null,
          notes: notes || null,
        },
      }),
    onSuccess: () => { onChanged(); onClose(); },
  });
  const snooze = useMutation({
    mutationFn: () => api(`/api/transactions/${txn.id}/snooze`, { method: 'POST' }),
    onSuccess: () => { onChanged(); onClose(); },
  });
  const ask = useMutation({
    mutationFn: () => api(`/api/transactions/${txn.id}/ask`, { method: 'POST', body: { toUserId: askTo } }),
    onSuccess: () => { onChanged(); onClose(); },
  });
  const answer = useMutation({
    mutationFn: (r: 'YES_MINE' | 'NOT_ME') =>
      api(`/api/transactions/${txn.id}/answer-ask`, { method: 'POST', body: { response: r } }),
    onSuccess: () => { onChanged(); onClose(); },
  });

  const isAsked = txn.attributionStatus === 'ASKED';
  const isMarked = txn.attributionStatus === 'MARKED';
  const amIAsked = isAsked && user?.id && txn.askState?.to?.id === user.id && !txn.askState?.response;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold">{formatTxnMoney(txn, 'both')}</h2>
            <div className="text-sm text-muted-foreground">{txn.instrumentLabel}</div>
            <div className="text-sm text-muted-foreground">{formatTime(txn.occurredAt)}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase text-muted-foreground">Debit details</div>
            <div className="mt-1 grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">Beneficiary:</span> {txn.beneficiary ?? '—'}</div>
              {txn.attribution?.type === 'OFFICE_EXPENSE' ? null : (
                <div><span className="text-muted-foreground">Vendor:</span> {txn.vendor ?? '—'}</div>
              )}
              <div><span className="text-muted-foreground">Instrument:</span> {txn.instrumentLabel}</div>
              <div><span className="text-muted-foreground">Type:</span> {txn.instrumentType}</div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase text-muted-foreground">Who did this</div>
            <div className="mt-1 text-sm">
              {txn.attributedBy?.name
                ? <>{txn.attributedBy.name} {txn.attributedBy.role && <span className="text-muted-foreground">· {txn.attributedBy.role}</span>}</>
                : <span className="text-muted-foreground">Unknown — attribution needed</span>}
            </div>
          </div>

          {isMarked ? (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground">Attribution</div>
              <div className="mt-1 text-sm text-green-700">✓ Marked</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Type:</span> {txn.attribution?.type ?? '—'}</div>
                {txn.attribution?.trip && <div><span className="text-muted-foreground">Trip:</span> {txn.attribution.trip.name ?? txn.attribution.trip.id}</div>}
                {txn.attribution?.client && <div><span className="text-muted-foreground">Client:</span> {txn.attribution.client.name ?? txn.attribution.client.id}</div>}
                {txn.attribution?.notes && <div className="col-span-2"><span className="text-muted-foreground">Notes:</span> {txn.attribution.notes}</div>}
              </div>
            </div>
          ) : amIAsked ? (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground">Attribution</div>
              <div className="mt-1 text-sm">
                {txn.askState?.from?.name ?? 'A teammate'} is asking: did you do this?
              </div>
              <div className="mt-2 flex gap-2">
                <Button onClick={() => answer.mutate('YES_MINE')}>Yes, I'll attribute</Button>
                <Button variant="outline" onClick={() => answer.mutate('NOT_ME')}>Not me</Button>
              </div>
            </div>
          ) : isAsked ? (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground">Attribution</div>
              <div className="mt-1 text-sm text-amber-700">🕐 Waiting on {txn.askState?.to?.name ?? 'teammate'}'s reply</div>
            </div>
          ) : (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground">Attribution</div>
              <div className="mt-1 mb-3 text-sm text-red-600">⚠ Unmarked — please attribute</div>

              <div className="space-y-2">
                {[
                  { v: 'CLIENT', label: 'Client trip' },
                  { v: 'VENDOR', label: 'Vendor payment' },
                  { v: 'TEAM_MEMBER', label: 'Team member' },
                  { v: 'PERSONAL', label: 'Personal' },
                  { v: 'OTHER', label: 'Other' },
                ].map((opt) => (
                  <label key={opt.v} className="flex cursor-pointer items-center gap-2">
                    <input type="radio" checked={mode === opt.v} onChange={() => setMode(opt.v as typeof mode)} />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
                <div className="pl-6 space-y-2">
                  {mode === 'CLIENT' && (
                    <>
                      <SearchableSelect
                        value={clientId}
                        onChange={(v) => { setClientId(v); if (v) setTripId(null); }}
                        options={clientOptions}
                        placeholder={clients.isLoading ? 'Loading clients…' : 'Pick a client'}
                        disabled={clients.isLoading}
                      />
                      <SearchableSelect
                        value={tripId}
                        onChange={setTripId}
                        options={tripOptions}
                        placeholder={trips.isLoading ? 'Loading trips…' : (clientId ? 'Pick a trip for this client' : 'Pick a trip (or pick a client first)')}
                        disabled={trips.isLoading}
                      />
                    </>
                  )}
                  {mode === 'VENDOR' && (
                    <Input placeholder="Vendor name" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
                  )}
                  {mode === 'TEAM_MEMBER' && (
                    <SearchableSelect
                      value={teamMemberUserId}
                      onChange={setTeamMemberUserId}
                      options={teamOptions}
                      placeholder={users.isLoading ? 'Loading team…' : 'Pick a team member'}
                      disabled={users.isLoading}
                    />
                  )}
                  {mode === 'OTHER' && (
                    <Input placeholder="What was this for?" value={freeText} onChange={(e) => setFreeText(e.target.value)} />
                  )}
                </div>
                <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={() => attribute.mutate()} disabled={attribute.isPending}>
                  {attribute.isPending ? 'Saving…' : 'Save & mark resolved'}
                </Button>
                <Button variant="outline" onClick={() => snooze.mutate()} disabled={snooze.isPending}>
                  Snooze 1 hour
                </Button>
              </div>

              <div className="mt-6 border-t pt-4">
                <div className="mb-2 text-xs text-muted-foreground">Not sure who did this?</div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <SearchableSelect
                      value={askTo}
                      onChange={setAskTo}
                      options={teamOptions}
                      placeholder={users.isLoading ? 'Loading team…' : 'Pick a teammate to ask'}
                      disabled={users.isLoading}
                    />
                  </div>
                  <Button variant="outline" disabled={!askTo} onClick={() => ask.mutate()}>
                    Ask teammate who did →
                  </Button>
                </div>
              </div>
            </div>
          )}

          {txn.debitSmsBody && (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground">Raw SMS</div>
              <div className="mt-1 rounded bg-muted p-2 text-xs">{txn.debitSmsBody}</div>
            </div>
          )}

          <div className="border-t pt-4">
            <RaiseRefundButton txn={txn} onDone={onChanged} />
          </div>
        </div>
      </div>
    </div>
  );
}

function RaiseRefundButton({ txn, onDone }: { txn: LedgerTxn; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        Log a refund from this debit
      </Button>
      {open ? (
        <RaiseRefundDialog
          parentId={txn.id}
          parentAmount={txn.amount ?? '0'}
          onClose={() => setOpen(false)}
          onDone={() => {
            onDone();
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function RaiseRefundDialog({
  parentId,
  parentAmount,
  onClose,
  onDone,
}: {
  parentId: string;
  parentAmount: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [expectedAmount, setExpectedAmount] = useState(parentAmount);
  const [instrument, setInstrument] = useState<'CARD' | 'BANK' | 'WALLET'>('CARD');
  const [refundCardId, setRefundCardId] = useState('');
  const [refundBankAccountId, setRefundBankAccountId] = useState('');
  const [refundWalletId, setRefundWalletId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cards = useQuery<{ id: string; nickname: string; lastFour: string }[]>({
    queryKey: ['cards'],
    queryFn: () => api('/api/cards'),
  });
  const banks = useQuery<{ id: string; nickname: string }[]>({
    queryKey: ['bank-accounts-list'],
    queryFn: () => api('/api/bank-accounts'),
  });
  const wallets = useQuery<{ id: string; name: string }[]>({
    queryKey: ['wallets'],
    queryFn: () => api('/api/wallets'),
  });
  const submit = useMutation({
    mutationFn: () =>
      api('/api/refund-notes', {
        method: 'POST',
        body: {
          transactionId: parentId,
          expectedAmount,
          reason: reason || undefined,
          refundCardId: instrument === 'CARD' ? refundCardId : undefined,
          refundBankAccountId: instrument === 'BANK' ? refundBankAccountId : undefined,
          refundWalletId: instrument === 'WALLET' ? refundWalletId : undefined,
        },
      }),
    onSuccess: onDone,
    onError: (e) => setError((e as ApiError).message || 'Could not raise refund note.'),
  });
  const instrumentValid =
    (instrument === 'CARD' && !!refundCardId) ||
    (instrument === 'BANK' && !!refundBankAccountId) ||
    (instrument === 'WALLET' && !!refundWalletId);
  const ready = /^\d+(\.\d{1,2})?$/.test(expectedAmount) && instrumentValid;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-3">Raise a refund note</h3>
        <p className="text-xs text-muted-foreground mb-4">
          Ops raises a note; accounts marks it credited when the credit lands. The credited amount can differ from expected.
        </p>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Expected refund amount</Label>
            <Input
              inputMode="decimal"
              value={expectedAmount}
              onChange={(e) => setExpectedAmount(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>Refund lands on</Label>
            <div className="inline-flex rounded-md border p-0.5">
              {(['CARD', 'BANK', 'WALLET'] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setInstrument(opt)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm ${
                    instrument === opt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {opt.charAt(0) + opt.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
          {instrument === 'CARD' ? (
            <select
              value={refundCardId}
              onChange={(e) => setRefundCardId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Select card —</option>
              {(cards.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.nickname} · ••{c.lastFour}</option>
              ))}
            </select>
          ) : instrument === 'BANK' ? (
            <select
              value={refundBankAccountId}
              onChange={(e) => setRefundBankAccountId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Select bank —</option>
              {(banks.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>{b.nickname}</option>
              ))}
            </select>
          ) : (
            <select
              value={refundWalletId}
              onChange={(e) => setRefundWalletId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">— Select wallet —</option>
              {(wallets.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          )}
          <div className="space-y-1">
            <Label>Reason (optional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Client cancelled Bali trip"
            />
          </div>
        </div>
        {error ? <p className="text-sm text-destructive mt-3">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!ready || submit.isPending} onClick={() => submit.mutate()}>
            {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Raise refund note
          </Button>
        </div>
      </div>
    </div>
  );
}

type RefundNoteRow = {
  id: string;
  transactionId: string;
  expectedAmount: string;
  currency: string;
  status: 'PENDING' | 'CREDITED' | 'CANCELLED';
  reason: string | null;
  raisedAt: string;
  raisedByName: string | null;
  parentAmount: string | null;
  parentVendor: string | null;
  parentClientName: string | null;
  refundInstrumentLabel: string | null;
  creditedAmount: string | null;
  creditedAt: string | null;
};

function RefundInboxPanel({ onChanged, canReconcile }: { onChanged: () => void; canReconcile: boolean }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'PENDING' | 'CREDITED' | 'CANCELLED'>('PENDING');
  const notes = useQuery<RefundNoteRow[]>({
    queryKey: ['refund-notes', filter],
    queryFn: () => api(`/api/refund-notes?status=${filter}&limit=200`),
    refetchInterval: 30_000,
  });
  const markCredited = useMutation({
    mutationFn: ({ id, amount }: { id: string; amount: string }) =>
      api(`/api/refund-notes/${id}/credited`, { method: 'POST', body: { creditedAmount: amount } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['refund-notes'] });
      onChanged();
    },
  });
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api(`/api/refund-notes/${id}/cancel`, { method: 'POST', body: { reason } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['refund-notes'] });
      onChanged();
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{canReconcile ? 'Refund notes' : 'My refund notes'}</CardTitle>
        <CardDescription>
          {canReconcile
            ? 'Promissory refund records raised by ops. Confirm the credit landed with the amount actually received (may differ).'
            : 'Refund notes you raised. Accounts confirms when the credit lands.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex gap-2">
          {(['PENDING', 'CREDITED', 'CANCELLED'] as const).map((s) => (
            <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm" onClick={() => setFilter(s)}>
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </Button>
          ))}
        </div>
        {notes.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (notes.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No refund notes in {filter.toLowerCase()}.</p>
        ) : (
          <div className="space-y-2">
            {(notes.data ?? []).map((n) => (
              <RefundNoteRowCard
                key={n.id}
                note={n}
                canReconcile={canReconcile}
                onMarkCredited={(amt) => markCredited.mutate({ id: n.id, amount: amt })}
                onCancel={(reason) => cancel.mutate({ id: n.id, reason })}
                busy={markCredited.isPending || cancel.isPending}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RefundNoteRowCard({
  note,
  onMarkCredited,
  onCancel,
  busy,
  canReconcile,
}: {
  note: RefundNoteRow;
  onMarkCredited: (amount: string) => void;
  onCancel: (reason?: string) => void;
  busy: boolean;
  canReconcile: boolean;
}) {
  const [showCredit, setShowCredit] = useState(false);
  const [amount, setAmount] = useState(note.expectedAmount);
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-medium">
            {formatMoney(note.expectedAmount, note.currency)} expected
            {note.parentClientName ? <span className="text-muted-foreground"> · {note.parentClientName}</span> : null}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {note.refundInstrumentLabel ?? '—'}
            {note.raisedByName ? ` · raised by ${note.raisedByName}` : ''}
            {note.reason ? ` · ${note.reason}` : ''}
          </div>
          {note.status === 'CREDITED' ? (
            <div className="text-xs text-emerald-700 mt-1">
              Credited {formatMoney(note.creditedAmount, note.currency)}
              {note.creditedAt ? ` on ${new Date(note.creditedAt).toLocaleDateString()}` : ''}
            </div>
          ) : null}
        </div>
        {note.status === 'PENDING' && canReconcile ? (
          <div className="flex gap-2 shrink-0">
            {showCredit ? (
              <>
                <Input
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-28 h-8 text-xs"
                />
                <Button size="sm" disabled={busy} onClick={() => onMarkCredited(amount)}>
                  Confirm
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowCredit(false)}>
                  Back
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" onClick={() => setShowCredit(true)}>Mark credited</Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => onCancel()}>
                  Cancel note
                </Button>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
